/**
 * Twitter-inspired feed scoring algorithm for SHXRE.
 *
 * Modeled after Twitter/X's open-source recommendation algorithm (home-mixer).
 * See: https://github.com/twitter/the-algorithm
 *
 * Pipeline stages (mirrors Twitter's ScoredTweets pipeline):
 *  1. Score  — engagement × recency × content × affinity × fatigue × network
 *  2. Sort   — highest score first
 *  3. Rescore — author diversity, content diversity, reply rescoring
 *  4. Inject  — content exploration (serendipity) at fixed positions
 *  5. Re-sort — final ordering
 *
 * Signals:
 *  - Engagement (likes, replies, reposts, quotes)  → Heavy ranker predictions
 *  - Recency decay                                 → Temporal features
 *  - Author diversity                              → AuthorBasedListwiseRescoring
 *  - Content diversity                             → MediaClusterBasedRescoring
 *  - Author affinity                               → RealGraph
 *  - Feedback fatigue (seen posts)                 → RescoreFeedbackFatigue
 *  - Negative feedback (not interested)            → FeedbackFatigueScorer
 *  - News / culture boost                          → Topic inference
 *  - In-network vs out-of-network                  → RescoreOutOfNetwork
 *  - Thread / reply scoring                        → RescoreReplies
 *  - Content exploration                           → ForYouExplorationTweets
 *  - Author credibility                            → TweepCred
 *  - Dwell time tracking                           → PredictedDwellScore
 */
import {
  type AppBskyFeedDefs,
  AppBskyEmbedImages,
  AppBskyEmbedVideo,
  AppBskyEmbedExternal,
  AppBskyEmbedRecord,
  AppBskyEmbedRecordWithMedia,
  AppBskyFeedPost,
} from '@atproto/api'

import {type FeedTunerFn, type FeedViewPostsSlice, FeedTuner} from './feed-manip'
import {getActionHistory} from '#/state/userActionHistory'
import {
  computeFeedbackPenalty,
  getAuthorAvgDwell,
  getAuthorServedCount,
  recordServedAuthor,
} from '#/state/feedAlgorithmState'

// ---------------------------------------------------------------------------
// View count estimation
// ---------------------------------------------------------------------------

/** Cache estimated-view base per URI; time bonus is computed fresh so counts grow. */
const viewCountBaseCache = new Map<string, number>()

const VIEWS_PER_HOUR = 10
/** ~3% engagement rate (Twitter ~2–5%); used to back-calculate views from engagement. */
const AVG_ENGAGEMENT_RATE = 0.03
const MIN_ESTIMATED_VIEWS = 1

function postHasVideo(post: AppBskyFeedDefs.PostView): boolean {
  const embed = post.embed
  if (!embed) return false
  if (AppBskyEmbedVideo.isView(embed)) return true
  if (AppBskyEmbedRecordWithMedia.isView(embed) && embed.media?.video) return true
  return false
}

function postHasLinkCard(post: AppBskyFeedDefs.PostView): boolean {
  const embed = post.embed
  if (!embed) return false
  return AppBskyEmbedExternal.isView(embed)
}

/**
 * View count for display: estimated views from engagement (original formula) plus 10 views/hour.
 * Estimate uses engagement rate (3% baseline; lower for larger followings) to back-calculate
 * views; then we add a time bonus so counts grow over time. Base is cached per URI.
 */
export function estimateViewCount(post: AppBskyFeedDefs.PostView): number {
  let base = viewCountBaseCache.get(post.uri)
  if (base === undefined) {
    const likes = post.likeCount ?? 0
    const replies = post.replyCount ?? 0
    const reposts = post.repostCount ?? 0
    const quotes = post.quoteCount ?? 0
    const totalEngagement = likes + replies + reposts + quotes

    if (totalEngagement === 0) {
      base = MIN_ESTIMATED_VIEWS
    } else {
      const followers = post.author.followersCount ?? 0
      let rate = AVG_ENGAGEMENT_RATE
      if (followers > 10000) rate = 0.015
      else if (followers > 1000) rate = 0.025
      const estimated = Math.round(totalEngagement / rate)
      base = Math.max(estimated, totalEngagement)
    }

    viewCountBaseCache.set(post.uri, base)
    if (viewCountBaseCache.size > 2000) {
      const firstKey = viewCountBaseCache.keys().next().value
      if (firstKey) viewCountBaseCache.delete(firstKey)
    }
  }

  const createdAt = new Date(post.indexedAt).getTime()
  const hoursSinceCreation = (Date.now() - createdAt) / (1000 * 60 * 60)
  const timeBonus = Math.max(0, Math.floor(hoursSinceCreation)) * VIEWS_PER_HOUR

  return base + timeBonus
}

/**
 * Format view count for display (e.g. "1.2K", "3.4M")
 */
export function formatViewCount(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  }
  return String(count)
}

// ---------------------------------------------------------------------------
// Content type detection (for diversity + topic boosting)
// ---------------------------------------------------------------------------

type ContentType =
  | 'text'
  | 'image'
  | 'video'
  | 'link'
  | 'quote'
  | 'news'
  | 'culture'
  | 'art'
  | 'political'

const NEWS_KEYWORDS = [
  'breaking', 'report', 'update', 'announced', 'official', 'source',
  'confirmed', 'exclusive', 'developing', 'statement', 'press',
  'investigation', 'election', 'government', 'politics', 'legislation',
  'court', 'ruling', 'verdict', 'police', 'arrest',
  'economy', 'inflation', 'recession', 'gdp', 'unemployment',
  'crisis', 'emergency', 'disaster', 'earthquake', 'hurricane',
  'war', 'conflict', 'military', 'sanctions', 'diplomacy',
  'congress', 'senate', 'democrat', 'republican', 'trump', 'biden',
  'white house', 'supreme court', 'policy', 'law', 'bill',
  'protest', 'rally', 'vote', 'ballot', 'campaign',
  'geopolitics', 'foreign policy', 'nato', 'united nations',
]

const CULTURE_KEYWORDS = [
  'album', 'movie', 'film', 'series', 'show', 'premiere',
  'music', 'song', 'artist', 'concert', 'tour', 'release',
  'fashion', 'style', 'brand', 'collab', 'collection',
  'game', 'gaming', 'esports', 'tournament', 'stream',
  'sports', 'nba', 'nfl', 'ufc', 'soccer', 'football',
  'celebrity', 'viral', 'trending', 'meme', 'culture',
  'anime', 'manga', 'comic', 'design',
  'tv', 'netflix', 'disney', 'hbo', 'marvel', 'dc',
  'hip hop', 'rap', 'r&b', 'pop', 'rock',
  'sneakers', 'jordan', 'nike', 'adidas', 'yeezy',
]

const POLITICAL_KEYWORDS = [
  'election', 'vote', 'voting', 'ballot', 'campaign', 'primary',
  'congress', 'senate', 'house', 'democrat', 'republican', 'gop',
  'trump', 'biden', 'white house', 'supreme court', 'scotus',
  'legislation', 'bill', 'law', 'policy', 'governor', 'mayor',
  'protest', 'rally', 'activism', 'political', 'politics',
  'geopolitics', 'foreign policy', 'diplomacy', 'sanctions',
  'impeach', 'cabinet', 'senator', 'representative', 'congressional',
]

const ART_KEYWORDS = [
  'photography', 'landscape', 'nature', 'sunset', 'sunrise',
  'mountains', 'river', 'lake', 'ocean', 'beach', 'forest',
  'flowers', 'scenic', 'panorama', 'waterfall', 'wildlife',
  'macro', 'photoshoot', 'photooftheday', 'picoftheday',
  'naturephotography', 'landscapephotography', 'travelphotography',
  'goldenhour', 'longexposure', 'streetphotography',
  '#photography', '#landscape', '#nature', '#sunset',
]

function detectContentTypes(
  post: AppBskyFeedDefs.PostView,
): Set<ContentType> {
  const types = new Set<ContentType>()
  const embed = post.embed

  if (AppBskyEmbedImages.isView(embed)) types.add('image')
  if (AppBskyEmbedVideo.isView(embed)) types.add('video')
  if (AppBskyEmbedExternal.isView(embed)) types.add('link')
  if (AppBskyEmbedRecord.isView(embed)) types.add('quote')
  if (AppBskyEmbedRecordWithMedia.isView(embed)) {
    types.add('quote')
    if (embed.media && AppBskyEmbedImages.isView(embed.media)) types.add('image')
    if (embed.media && AppBskyEmbedVideo.isView(embed.media)) types.add('video')
  }

  if (types.size === 0) types.add('text')

  const text = getPostText(post).toLowerCase()
  if (text) {
    if (NEWS_KEYWORDS.some(kw => text.includes(kw))) types.add('news')
    if (CULTURE_KEYWORDS.some(kw => text.includes(kw))) types.add('culture')
    if (POLITICAL_KEYWORDS.some(kw => text.includes(kw))) types.add('political')
    if (ART_KEYWORDS.some(kw => text.includes(kw))) types.add('art')
  }

  return types
}

function getPostText(post: AppBskyFeedDefs.PostView): string {
  if (AppBskyFeedPost.isRecord(post.record)) {
    return (post.record as AppBskyFeedPost.Record).text ?? ''
  }
  return ''
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

const WEIGHT_LIKE = 1.0
const WEIGHT_REPLY = 1.5
const WEIGHT_REPOST = 1.3
const WEIGHT_QUOTE = 2.0

const RECENCY_HALF_LIFE_HOURS = 6
const NEWS_BOOST = 4.0
const CULTURE_BOOST = 4.0 // boosted: culture content ranked higher
const POLITICAL_BOOST = 4.5 // political content ranked higher
const VIDEO_BOOST = 1.3
const IMAGE_BOOST = 1.1
const LINK_BOOST = 1.05
const ART_PENALTY = 0.15            // art/photography posts ranked much lower

const AUTHOR_AFFINITY_BOOST = 1.5  // boost authors you've liked/followed (RealGraph)
const SEEN_PENALTY = 0.3           // down-rank posts you've already seen
const AUTHOR_DIVERSITY_DECAY = 0.6 // each consecutive post by same author gets 60% of prev score

// New: In-network vs out-of-network (Twitter's RescoreOutOfNetwork)
const OON_SCALE_FACTOR = 0.75     // out-of-network tweets get 75% weight
const IN_NETWORK_BOOST = 1.15     // slight boost for in-network

// New: Thread/reply scoring (Twitter's RescoreReplies)
const REPLY_SCALE_FACTOR = 0.85    // replies slightly down-ranked by default
const HIGH_ENGAGEMENT_REPLY_BOOST = 1.3 // but high-engagement replies get boosted

// New: Dwell time scoring (Twitter's PredictedDwellScore)
const HIGH_DWELL_AUTHOR_BOOST = 1.25  // boost authors you spend time reading
const LOW_DWELL_AUTHOR_PENALTY = 0.85 // penalize authors you scroll past

// New: Content exploration (Twitter's ForYouExplorationTweets)
const EXPLORATION_INTERVAL = 12    // inject exploration post every N posts

// Priority authors: posts from these DIDs are ranked much higher (Following + Discover)
const PRIORITY_AUTHOR_DIDS = new Set([
  'did:plc:iwwwmzfzoa5ec6pzznjc4gpk',
  'did:plc:3hbjvtj5pwcpsytjh3moov7h',
])
const PRIORITY_AUTHOR_BOOST = 6.0 // strong boost so they surface near the top

/**
 * Score a single post slice. Higher = ranked higher in feed.
 * Inspired by Twitter's ScoredTweetsModelScoringPipelineConfig.
 */
function scoreSlice(
  slice: FeedViewPostsSlice,
  seenUris: Set<string>,
  likedAuthorDids: Set<string>,
  followedDids: Set<string>,
): number {
  const post = slice.items[slice.items.length - 1]?.post
  if (!post) return 0

  // 1. Base engagement score (Twitter's heavy ranker output approximation)
  const likes = post.likeCount ?? 0
  const replies = post.replyCount ?? 0
  const reposts = post.repostCount ?? 0
  const quotes = post.quoteCount ?? 0

  const engagementScore =
    likes * WEIGHT_LIKE +
    replies * WEIGHT_REPLY +
    reposts * WEIGHT_REPOST +
    quotes * WEIGHT_QUOTE

  const normalizedEngagement = Math.log1p(engagementScore)

  // 2. Recency decay (Twitter's temporal features)
  const postAge = Date.now() - new Date(post.indexedAt).getTime()
  const hoursOld = postAge / (1000 * 60 * 60)
  const recencyMultiplier = Math.pow(0.5, hoursOld / RECENCY_HALF_LIFE_HOURS)

  // 3. Content type boosts (Twitter's topic/content scoring)
  const contentTypes = detectContentTypes(post)
  let contentMultiplier = 1.0
  if (contentTypes.has('art')) contentMultiplier *= ART_PENALTY
  if (contentTypes.has('news')) contentMultiplier *= NEWS_BOOST
  if (contentTypes.has('culture')) contentMultiplier *= CULTURE_BOOST
  if (contentTypes.has('political')) contentMultiplier *= POLITICAL_BOOST
  if (contentTypes.has('video')) contentMultiplier *= VIDEO_BOOST
  if (contentTypes.has('image')) contentMultiplier *= IMAGE_BOOST
  if (contentTypes.has('link')) contentMultiplier *= LINK_BOOST

  // 4. Author affinity (Twitter's RealGraph — boost authors you interact with)
  let affinityMultiplier = 1.0
  const authorDid = post.author.did
  if (likedAuthorDids.has(authorDid) || followedDids.has(authorDid)) {
    affinityMultiplier = AUTHOR_AFFINITY_BOOST
  }

  // 4b. Priority author boost — rank posts from priority DIDs much higher
  if (PRIORITY_AUTHOR_DIDS.has(authorDid)) {
    affinityMultiplier *= PRIORITY_AUTHOR_BOOST
  }

  // 5. Feedback fatigue (Twitter's RescoreFeedbackFatigue — penalize seen content)
  let fatiguePenalty = 1.0
  if (seenUris.has(post.uri)) {
    fatiguePenalty = SEEN_PENALTY
  }

  // 6. Author credibility boost (Twitter's TweepCred — PageRank approximation)
  const followers = post.author.followersCount ?? 0
  const following = post.author.followsCount ?? 1
  const ratio = followers / Math.max(following, 1)
  const credibility = Math.min(Math.log1p(ratio), 3) / 3
  const credibilityMultiplier = 0.8 + credibility * 0.4

  // 7. NEW: In-network vs out-of-network (Twitter's RescoreOutOfNetwork)
  const isInNetwork = followedDids.has(authorDid) || post.author.viewer?.following
  const networkMultiplier = isInNetwork ? IN_NETWORK_BOOST : OON_SCALE_FACTOR

  // 8. NEW: Negative feedback (Twitter's FeedbackFatigueScorer)
  const feedbackPenalty = computeFeedbackPenalty(authorDid, post.uri)

  // 9. NEW: Thread/reply scoring (Twitter's RescoreReplies)
  let replyMultiplier = 1.0
  if (slice.isReply) {
    const totalEngagement = likes + replies + reposts + quotes
    if (totalEngagement > 20) {
      replyMultiplier = HIGH_ENGAGEMENT_REPLY_BOOST
    } else {
      replyMultiplier = REPLY_SCALE_FACTOR
    }
  }

  // 10. NEW: Dwell time scoring (Twitter's PredictedDwellScore)
  let dwellMultiplier = 1.0
  const avgDwell = getAuthorAvgDwell(authorDid)
  if (avgDwell !== null) {
    if (avgDwell > 3000) {
      dwellMultiplier = HIGH_DWELL_AUTHOR_BOOST
    } else if (avgDwell < 800) {
      dwellMultiplier = LOW_DWELL_AUTHOR_PENALTY
    }
  }

  // Final score
  return (
    normalizedEngagement *
    recencyMultiplier *
    contentMultiplier *
    affinityMultiplier *
    fatiguePenalty *
    credibilityMultiplier *
    networkMultiplier *
    feedbackPenalty *
    replyMultiplier *
    dwellMultiplier
  )
}

// ---------------------------------------------------------------------------
// Author diversity reranking (Twitter's AuthorBasedListwiseRescoring)
// ---------------------------------------------------------------------------

function applyAuthorDiversity(
  slices: FeedViewPostsSlice[],
): FeedViewPostsSlice[] {
  const authorPositionCount = new Map<string, number>()

  for (const slice of slices) {
    const post = slice.items[slice.items.length - 1]?.post
    if (!post) continue

    const authorDid = post.author.did
    const count = authorPositionCount.get(authorDid) ?? 0
    authorPositionCount.set(authorDid, count + 1)

    if (count > 0) {
      // Apply exponential decay for repeated authors
      ;(slice as any)._score *= Math.pow(AUTHOR_DIVERSITY_DECAY, count)
    }
  }

  return slices
}

// ---------------------------------------------------------------------------
// Content diversity (Twitter's MediaClusterBasedRescoring)
// Ensures we don't show 5 text posts in a row or 5 images in a row
// ---------------------------------------------------------------------------

function applyContentDiversity(
  slices: FeedViewPostsSlice[],
): FeedViewPostsSlice[] {
  let lastContentType: ContentType | null = null
  let sameTypeStreak = 0

  for (const slice of slices) {
    const post = slice.items[slice.items.length - 1]?.post
    if (!post) continue

    const types = detectContentTypes(post)
    const primaryType = types.has('video')
      ? 'video'
      : types.has('image')
        ? 'image'
        : types.has('link')
          ? 'link'
          : 'text'

    if (primaryType === lastContentType) {
      sameTypeStreak++
      if (sameTypeStreak >= 3) {
        ;(slice as any)._score *= 0.7 // penalize 4th+ of same type in a row
      }
    } else {
      sameTypeStreak = 0
    }
    lastContentType = primaryType
  }

  return slices
}

// ---------------------------------------------------------------------------
// Main feed scoring tuner function
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Content exploration (Twitter's ForYouExplorationTweets)
// Injects posts from under-served followed authors to prevent echo chambers
// ---------------------------------------------------------------------------

function applyContentExploration(
  slices: FeedViewPostsSlice[],
  followedDids: Set<string>,
): FeedViewPostsSlice[] {
  if (slices.length < EXPLORATION_INTERVAL) return slices

  const underServedSlices: FeedViewPostsSlice[] = []
  const mainSlices: FeedViewPostsSlice[] = []

  for (const slice of slices) {
    const post = slice.items[slice.items.length - 1]?.post
    if (!post) {
      mainSlices.push(slice)
      continue
    }

    const authorDid = post.author.did
    const servedCount = getAuthorServedCount(authorDid)
    const isFollowed = followedDids.has(authorDid) || !!post.author.viewer?.following

    if (isFollowed && servedCount === 0) {
      underServedSlices.push(slice)
    } else {
      mainSlices.push(slice)
    }
  }

  if (underServedSlices.length === 0) return slices

  // Inject under-served author posts at regular intervals
  const result: FeedViewPostsSlice[] = []
  let explorationIdx = 0

  for (let i = 0; i < mainSlices.length; i++) {
    result.push(mainSlices[i])

    if (
      (i + 1) % EXPLORATION_INTERVAL === 0 &&
      explorationIdx < underServedSlices.length
    ) {
      result.push(underServedSlices[explorationIdx])
      explorationIdx++
    }
  }

  // Append any remaining exploration posts
  while (explorationIdx < underServedSlices.length) {
    result.push(underServedSlices[explorationIdx])
    explorationIdx++
  }

  return result
}

// ---------------------------------------------------------------------------
// Main feed scoring tuner function
// ---------------------------------------------------------------------------

/**
 * Twitter-inspired feed ranking tuner for SHXRE.
 *
 * Full pipeline (mirrors Twitter's ScoredTweets pipeline):
 * 1. Score each post (engagement × recency × content × affinity × fatigue
 *    × network × feedback × reply × dwell)
 * 2. Sort by score descending
 * 3. Apply author diversity (exponential decay for repeated authors)
 * 4. Apply content diversity (penalize same-type streaks)
 * 5. Re-sort by adjusted score
 * 6. Inject content exploration (under-served followed authors)
 * 7. Track served authors for session-level diversity
 */
export function twitterStyleRanking(): FeedTunerFn {
  return (
    _tuner: FeedTuner,
    slices: FeedViewPostsSlice[],
    dryRun: boolean,
  ): FeedViewPostsSlice[] => {
    if (dryRun || slices.length <= 1) return slices

    // Gather user interaction history for affinity scoring
    const history = getActionHistory()
    const seenUris = new Set(history.seen.map(s => s.uri))
    const likedAuthorDids = new Set<string>()
    const followedDids = new Set(history.follows)

    // Stage 1: Score each slice
    for (const slice of slices) {
      ;(slice as any)._score = scoreSlice(slice, seenUris, likedAuthorDids, followedDids)
    }

    // Stage 2: Sort by score (highest first)
    slices.sort((a, b) => ((b as any)._score ?? 0) - ((a as any)._score ?? 0))

    // Stage 3: Apply diversity rescoring (Twitter's listwise rescorers)
    applyAuthorDiversity(slices)
    applyContentDiversity(slices)

    // Stage 4: Re-sort after diversity adjustments
    slices.sort((a, b) => ((b as any)._score ?? 0) - ((a as any)._score ?? 0))

    // Stage 5: Content exploration injection
    slices = applyContentExploration(slices, followedDids)

    // Stage 6: Track served authors for session-level diversity
    for (const slice of slices) {
      const post = slice.items[slice.items.length - 1]?.post
      if (post) {
        recordServedAuthor(post.author.did)
      }
    }

    // Debug: log ranking results to console (remove in production)
    if (typeof console !== 'undefined' && slices.length > 0) {
      console.log(
        `[SHXRE Algorithm] Ranked ${slices.length} posts:`,
        slices.slice(0, 5).map((s, i) => {
          const p = s.items[s.items.length - 1]?.post
          if (!p) return null
          const text = getPostText(p).slice(0, 50)
          return `#${i + 1} score=${((s as any)._score ?? 0).toFixed(2)} | @${p.author.handle} | "${text}…" | ❤${p.likeCount ?? 0} 💬${p.replyCount ?? 0} 🔁${p.repostCount ?? 0}`
        }),
      )
    }

    return slices
  }
}
